// Madrid wall-clock ⇄ instant helpers (pure, no dependency). CARO publishes for
// Spain first: Edit articles (lib/articles) and finds drops (/admin → Hallazgos)
// are scheduled in Madrid time and stored as ISO 8601 with an explicit offset
// (+01:00 in winter, +02:00 in summer), the shape `isValidPublishAt` accepts.

export const MADRID_TIME_ZONE = 'Europe/Madrid';

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/** Offset (ms) of Madrid's wall clock from UTC at the instant `ms`. */
export function madridOffset(ms: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MADRID_TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(ms));
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second')) - ms;
}

/** «+02:00» / «+01:00» for an offset in ms. */
function offsetSuffix(off: number): string {
  const sign = off < 0 ? '-' : '+';
  const mins = Math.abs(Math.round(off / 60_000));
  return `${sign}${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * A Madrid wall-clock time as typed in `<input type="datetime-local">`
 * («2026-11-05T19:00», seconds optional) → ISO with Madrid's offset at that
 * moment («2026-11-05T19:00:00+01:00»). Clock changes (last Sunday of March /
 * October, at 02:00 / 03:00 local):
 * - spring gap (e.g. 2026-03-29 02:30 does not exist in Madrid) → null, so the
 *   form asks for another time instead of silently shifting it;
 * - autumn overlap (2026-10-25 02:30 happens twice) → the FIRST occurrence
 *   (summer time, +02:00), the earlier instant.
 * Malformed or out-of-range input → null. Pure.
 */
export function madridLocalToIso(local: string): string | null {
  const m = LOCAL_RE.exec(local.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s = '00'] = m;
  const wall = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
  // Date.UTC rolls over out-of-range parts (month 13, 25:00): refuse those.
  const canonical = `${y}-${mo}-${d}T${h}:${mi}:${s}`;
  if (!Number.isFinite(wall) || new Date(wall).toISOString().slice(0, 19) !== canonical) return null;
  // Madrid's offset is one of the values a day either side; a candidate is
  // valid when the instant it yields really shows that offset.
  const candidates = [...new Set([madridOffset(wall - DAY_MS), madridOffset(wall), madridOffset(wall + DAY_MS)])];
  const valid = candidates
    .map((off) => ({ off, at: wall - off }))
    .filter(({ off, at }) => madridOffset(at) === off)
    .sort((a, b) => a.at - b.at);
  const first = valid[0];
  if (!first) return null; // inside the spring-forward gap
  return `${canonical}${offsetSuffix(first.off)}`;
}

/**
 * An ISO instant (any offset or Z) → the Madrid wall-clock value of a
 * `datetime-local` input («2026-11-05T19:00», minutes precision). '' for an
 * empty or unparseable value. Inverse of madridLocalToIso. Pure.
 */
export function isoToMadridLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  return new Date(ms + madridOffset(ms)).toISOString().slice(0, 16);
}
